import { useCallback, useEffect, useRef } from "react";

/**
 * Custom hook that sets up a timeout and provides functions to reset and clear the timeout.
 *
 * @param callback - The callback function to be executed when the timeout expires.
 * @param delayInMilliseconds - The delay in milliseconds before the callback is invoked.
 * @returns An object containing the `reset` and `clear` functions.
 */
export function useTimeout(
  callback: (...args: any[]) => void,
  delayInMilliseconds: number
) {
  const callbackRef = useRef(callback);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const set = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      callbackRef.current();
    }, delayInMilliseconds);
  }, [delayInMilliseconds]);

  const clear = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    set();
    return clear;
  }, [set, clear]);

  const reset = useCallback(() => {
    clear();
    set();
  }, [clear, set]);

  return { reset, clear };
}
